// **React Imports
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

// **MUI Imports
import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  LinearProgress,
  Typography
} from '@mui/material'

// **Components Imports
import Radar from '../../../../../components/charts/radar'
import Icon from '../../../../../components/icon'

// **API Imports
import { getAIScoreMetrics } from '../../../../../axios/api/contest'

// **Utils Imports
import { showErrorToast } from '../../../../../utils/utils'

// **Third party Imports
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Chip from '../../../../../components/chip'
import CircularProgressBar from '../../../../../components/circular-progress-bar'
import FileIcon from '../../../../../components/editor/FileIcon'
import {
  CHALLENGE_TYPES,
  languagesLogoFromLabel
} from '../../../../../utils/constant'
import './AIEvaluation.scss'
// **Constant
const PROGRESS_COLOR = [
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#eab308', // Yellow/Gold
  '#a855f7', // Purple
  '#15b800', // Green
  '#f97316', // Orange
  '#06b6d4', // Cyan/Teal
  '#4d5ac7ff', // Deep Violet
  '#01eb57', // Bright Lime
  '#abce00' // Chartreuse
]

// Helper components
const Loader = memo(() => {
  return (
    <Box className="loader">
      <CircularProgress />
      <Box className="desc">
        <Icon icon={'fluent-color:text-bullet-list-square-sparkle-16'} />
        <Typography>Please wait while we are preparing your report.</Typography>
      </Box>
    </Box>
  )
})

const ProgressBar = memo(({ data, color }) => {
  return (
    <Grid
      item
      xs={12}
      md={6}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography fontSize={'11px'}>{data?.field}</Typography>
        <Typography fontWeight={'bold'} color={color} fontSize={'11px'}>
          {data?.score}/10
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        sx={{
          height: '5px',
          borderRadius: 5,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color
          }
        }}
        value={(data?.score ?? 0) * 10}
      />
    </Grid>
  )
})

const Main = memo(({ data }) => {
  // variables
  const applicationLayout = useMemo(() => {
    return (
      data?.challengeType === CHALLENGE_TYPES.APPLICATION ||
      data?.challengeType === CHALLENGE_TYPES.NOTEBOOK
    )
  }, [data?.challengeType])

  return (
    <Box
      className={`main-container ${
        applicationLayout ? 'application-stage' : ''
      }`}
    >
      <Box className="header">
        <Typography className="heading">AI Code Evaluation</Typography>
        <Typography
          variant="body2"
          fontSize={'12px'}
          color={'var(--text-secondary)'}
        >
          Comprehensive analysis of code quality and performance
        </Typography>
      </Box>

      <Box className="score_summary">
        <Box className="overall-score">
          <Typography className="label" sx={{ mb: 2 }}>
            Overall Score
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <CircularProgressBar
              size={100}
              value={data?.overallScore}
              thickness={3}
            />
            <Box className="score-text">
              <Typography className="score">
                <Typography component={'span'} className="score-number">
                  {data?.overallScore}
                </Typography>
                <Typography className="score-out-of" component={'span'}>
                  out of {data?.totalScore || '100'}
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="summary-info">
          <Grid container spacing={4}>
            {data?.languageLabel && (
              <Grid item md={6} xs={12} className="language-container">
                <Typography className="label" sx={{ mb: 2 }}>
                  Language or Framework
                </Typography>
                <Box className="language-box">
                  <FileIcon
                    iconName={languagesLogoFromLabel?.[data?.language]}
                    maxWidth={applicationLayout ? '34px' : '20px'}
                  />
                  <Typography
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      lineHeight: 1
                    }}
                  >
                    <Typography component={'span'}>
                      {data?.languageLabel}
                    </Typography>
                    {data?.subTypeLabel && (
                      <Typography component={'span'} variant="body2">
                        {data?.subTypeLabel}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}
            {data?.database && (
              <Grid item md={6} xs={12} className="language-container">
                <Typography className="label" sx={{ mb: 2 }}>
                  Database
                </Typography>
                <Box className="language-box">
                  <FileIcon
                    iconName={languagesLogoFromLabel?.[data?.database]}
                    maxWidth={applicationLayout ? '34px' : '20px'}
                  />
                  <Typography
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      lineHeight: 1
                    }}
                  >
                    <Typography component={'span'}>
                      {data?.databaseLabel}
                    </Typography>
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {data?.resultStatus && applicationLayout && (
            <Chip
              label={data?.resultStatus}
              variant="outlined"
              color="warning"
              icon={<Icon icon={'humbleicons:ai'} />}
            />
          )}
          <Grid container spacing={4} className="widget-container">
            {data?.timeComplexity && (
              <Grid item xs={6}>
                <Box className="widget">
                  <Icon
                    icon={'fluent-color:clock-32'}
                    style={{ color: 'var(--primary-main)', fontSize: '28px' }}
                  />
                  <Box className="desc">
                    <Typography className="widget-heading">
                      Time Complexity
                    </Typography>
                    <Typography className="widget-value">
                      {data?.timeComplexity}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {data?.spaceComplexity && (
              <Grid item xs={6}>
                <Box className="widget">
                  <Icon
                    icon={'fluent-color:coin-multiple-16'}
                    style={{ color: 'var(--primary-main)', fontSize: '28px' }}
                  />
                  <Box className="desc">
                    <Typography className="widget-heading">
                      Space Complexity
                    </Typography>
                    <Typography className="widget-value">
                      {data?.spaceComplexity}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
      <Box className="summary-container">
        <Icon
          icon={'fluent-color:bot-sparkle-16'}
          style={{ fontSize: '38px' }}
        />
        <Box>
          <Typography className="summary-heading">Quick Summary</Typography>
          <Typography fontSize={'12px'}>{data?.summary}</Typography>
        </Box>
      </Box>
      <Grid
        container
        spacing={4}
        alignItems={'stretch'}
        className="main-details"
      >
        <Grid item xs={12}>
          <Box className="detailed-metrics">
            <Box className="heading-section">
              <Icon
                icon={'material-symbols:target'}
                style={{ color: 'var(--info-main)' }}
              />
              <Typography>Detailed Metrics</Typography>
            </Box>
            <Grid rowGap={4} columnSpacing={4} container>
              {data?.metrics?.map((item, index) => {
                return (
                  <ProgressBar
                    key={item?.field}
                    data={item}
                    color={PROGRESS_COLOR?.[index] || PROGRESS_COLOR[0]}
                  />
                )
              })}
            </Grid>
          </Box>
        </Grid>
        {data?.metrics && data?.metrics?.length > 0 && (
          <Grid item xs={12}>
            <Box className="chart-container">
              <Box className="heading-section">
                <Icon
                  icon={'eva:flash-outline'}
                  style={{ color: 'var(--warning-main)' }}
                />
                <Typography>Performance Radar</Typography>
              </Box>

              <Box sx={{ height: '250px' }}>
                <Radar
                  indicators={(data?.metrics || [])?.map((item) => item?.field)}
                  values={(data?.metrics || [])?.map((item) => item?.score)}
                  name={'Performance Metrics'}
                  showFullLengthTitle={applicationLayout}
                />
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  )
})

const AIEvaluation = ({
  open,
  handleClose,
  challengeId,
  attemptId: propAttemptId = null
}) => {
  // hooks
  const { attemptId: paramAttemptId } = useParams()

  // states
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState({
    mainLoading: false,
    apiLoading: false
  })

  // variables
  const attemptId = useMemo(() => {
    return propAttemptId || paramAttemptId || null
  }, [propAttemptId, paramAttemptId])

  // refs
  const intervalIdRef = useRef(null)
  const startTimeRef = useRef(null)

  // handlers
  const stopPolling = useCallback(() => {
    clearInterval(intervalIdRef.current)
    setLoading({ mainLoading: false, apiLoading: false })
    toast.error(
      'AI code evaluation not available. Please try again after some time.'
    )
    handleClose()
  }, [handleClose])

  const fetchData = useCallback(async () => {
    if (!startTimeRef.current || Date.now() - startTimeRef.current > 60000) {
      stopPolling()
      return
    }

    setLoading((prev) => ({ ...prev, apiLoading: true }))
    const response = await getAIScoreMetrics(attemptId, challengeId)
    setLoading((prev) => ({ ...prev, apiLoading: false }))

    if (response?.isError) {
      showErrorToast(response)
    } else {
      if (response?.status === 'NOT_AVAILABLE') {
        clearInterval(intervalIdRef?.current)

        setLoading((prev) => ({ ...prev, mainLoading: false }))

        toast.error('AI code evaluation not available.')
        handleClose()
        return
      }

      if (response?.status === 'AVAILABLE') {
        clearInterval(intervalIdRef.current)
        setData(response?.codeQualityMetrics?.[0] || {})
        setLoading((prev) => ({ ...prev, mainLoading: false }))
      }
    }
  }, [attemptId, challengeId, stopPolling, handleClose])

  useEffect(() => {
    if (open) {
      setLoading({ mainLoading: true, apiLoading: false })
      setData(null)
      startTimeRef.current = Date.now()

      intervalIdRef.current = setInterval(fetchData, 2000)
    } else {
      clearInterval(intervalIdRef.current)
      setLoading({ mainLoading: false, apiLoading: false })
      setData(null)
    }

    return () => clearInterval(intervalIdRef.current)
  }, [open, fetchData])

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiPaper-root': { width: '100%', maxWidth: '850px' },
        zIndex: 2001
      }}
    >
      <Box
        className="ai-metrics-drawer-inner-container"
        sx={{ width: '100%', height: '100%' }}
      >
        {loading?.mainLoading ? <Loader /> : <Main data={data} />}
      </Box>
    </Drawer>
  )
}

export default memo(AIEvaluation)
